import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, Utils } from 'app/main/components';
import { useModel, useSettings, useUserData } from 'app/main/components/hooks';
import {
	BooleanField,
	CurrencyTextField,
	DateField,
	ModuleSearchField,
	NumberTextField,
	SearchSelectField,
	SelectField
} from 'app/main/components/fields';
import type { MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';

interface KpisGoalsEditDialogProps {
	model?: any;
	aggregation?: {
		module: string;
		value: string;
	};
	isAdd?: boolean;
	updateRefresh: VoidFunction;
}

export function showKpisGoalsEditDialog(props: KpisGoalsEditDialogProps) {
	return openDialog({
		children: <KpisGoalsEditDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});
}

export default function KpisGoalsEditDialog(props: KpisGoalsEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('sys_kpis_goals');
	const { settings: statusesSettings } = useSettings('sys_kpis_goals_statuses');
	const userData = useUserData();

	const { model, sendChange, save } = useModel({
		baseModel: {
			...(props.isAdd
				? { kpgl_uid: uuidv4(), kpgl_user_created_uid: userData.user_uid }
				: { kpgl_uid: props.model.kpgl_uid, kpgl_user_modified_uid: userData.user_uid })
		},
		model: props.model,
		settings,
		isEdit: true,
		isAdd: props.isAdd
	});

	function saveModel() {
		save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t));
				dispatch(closeDialog());
				props.updateRefresh();
			}
		});
	}

	function afterKpiChange(newValue) {
		sendChange('kpis_aggregation_module', newValue?.kpis_aggregation_module || null);
		sendChange('kpgl_aggregation_module_value', props.aggregation?.value || null);
		sendChange('kpis_format', newValue?.kpis_format || null);
	}

	const kpisFilters: MiddlewhereFilter[] | undefined = props.aggregation?.module
		? [{ filt_field: 'kpis_aggregation_module', filt_clause: 'equals', filt_value: props.aggregation?.module }]
		: undefined;

	return (
		<>
			<DialogTitle>
				{t(props.isAdd ? 'DIALOG_KPIS_GOALS_DIALOG_ADD_TITLE' : 'DIALOG_KPIS_GOALS_DIALOG_EDIT_TITLE')}
			</DialogTitle>
			<DialogContent>
				<Form id="kpisGoalsEditDialog" onSubmit={saveModel}>
					<Grid container spacing={1}>
						<Grid size={{ xs: 12 }}>
							{/* KPI */}
							<ModuleSearchField
								name="kpgl_kpis_uid"
								label={t('DETAILS_KPIS_GOALS_FIELD_KPI')}
								module="sys_kpis"
								filters={kpisFilters}
								uid={model.kpgl_kpis_uid}
								isEdit
								sendChange={sendChange}
								afterChange={afterKpiChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{!!model.kpis_aggregation_module && (
							<Grid size={{ xs: 12 }}>
								{/* AGGREGATION MODULE VALUE */}
								{/* If aggregation value is null, the goal is applied to all the module elements */}
								{props.aggregation?.value ? (
									<BooleanField
										name="applyToAll"
										label={t('DETAILS_KPIS_GOALS_FIELD_MODULE_APPLYTOALL')}
										value={!model.kpgl_aggregation_module_value}
										isEdit
										sendChange={(_, newValue) =>
											sendChange(
												'kpgl_aggregation_module_value',
												!newValue ? props.aggregation?.value : null
											)
										}
									/>
								) : (
									<ModuleSearchField
										name="kpgl_aggregation_module_value"
										label={t(`MODULE_${model.kpis_aggregation_module}_NAME_SINGULAR`)}
										module={model.kpis_aggregation_module}
										uid={model.kpgl_aggregation_module_value}
										isEdit
										sendChange={sendChange}
									/>
								)}
							</Grid>
						)}
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* FROM DATE */}
							<DateField
								name="kpgl_d_from"
								label={t('DETAILS_KPIS_GOALS_FIELD_DATE_FROM')}
								value={model.kpgl_d_from}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* TO DATE */}
							<DateField
								name="kpgl_d_to"
								label={t('DETAILS_KPIS_GOALS_FIELD_DATE_TO')}
								value={model.kpgl_d_to}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* CLAUSE */}
							<SelectField
								name="kpgl_clause"
								label={t('DETAILS_KPIS_GOALS_FIELD_CLAUSE')}
								options={[
									{ value: 'greater', title: t('DETAILS_KPIS_GOALS_FIELD_CLAUSE_GREATER') },
									{ value: 'smaller', title: t('DETAILS_KPIS_GOALS_FIELD_CLAUSE_SMALLER') }
									// { value: 'equals', title: t('DETAILS_KPIS_GOALS_FIELD_CLAUSE_EQUALS') }
								]}
								value={model.kpgl_clause}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* VALUE */}
							{model.kpis_format === 'currency' ? (
								<CurrencyTextField
									name="kpgl_value"
									label={t('DETAILS_KPIS_GOALS_FIELD_TARGET_VALUE')}
									value={model.kpgl_value}
									isEdit
									sendChange={sendChange}
									required
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							) : (
								<NumberTextField
									name="kpgl_value"
									label={t('DETAILS_KPIS_GOALS_FIELD_TARGET_VALUE')}
									value={model.kpgl_value}
									isEdit
									sendChange={sendChange}
									required
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							)}
						</Grid>
						{!statusesSettings.modu_disabled && (
							<Grid size={{ sm: 6, xs: 12 }}>
								{/* STATUS */}
								<SearchSelectField
									name="kpgl_kgst_id"
									label={t('DETAILS_KPIS_GOALS_FIELD_STATUS')}
									systable="sys_kpis_goals_statuses"
									value={model.kpgl_kgst_id}
									isEdit
									sendChange={sendChange}
								/>
							</Grid>
						)}
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="kpisGoalsEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
